import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Card,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { Autocomplete } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { format } from "date-fns";
import api from "../../utils/api";

const DEFAULT_STEPS = ["Property Info", "Violation Details", "Attachments", "Review"];

const now = new Date();
const localISO = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
  .toISOString()
  .slice(0, 16);

const AddViolationForm = ({
  violationTypes = [],
  propertyId,
  accountId,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formConfig, setFormConfig] = useState(null);

  useEffect(() => {
    api.get("/vio-form-config").then(r => {
      if (r.data && Object.keys(r.data).length > 0) setFormConfig(r.data);
    }).catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    ccr_violation_type_id: null,
    violated_at: localISO,
    comment: "",
    property_id: String(propertyId || "").trim(),
    account_id: String(accountId || "").trim(),
    cure_by: "",
  });

  const [selectedType, setSelectedType] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyViolationId, setNotifyViolationId] = useState(null);
  const [candidateEmails, setCandidateEmails] = useState({
    owner: [],
    internal: [],
  });
  const [selectedOwnerEmails, setSelectedOwnerEmails] = useState([]);
  const [selectedInternalEmails, setSelectedInternalEmails] = useState([]);
  const [emailPreview, setEmailPreview] = useState({ subject: "", html: "" });
  const [sendingNotify, setSendingNotify] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // cure_by is now always derived from the JWT on the backend - no localStorage needed

  useEffect(() => {
    setFormData((s) => ({
      ...s,
      property_id: String(propertyId || "").trim(),
      account_id: String(accountId || "").trim(),
    }));
  }, [propertyId, accountId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles?.length) return;
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
    },
  });

  const toggleEmail = (setter, currentList, email) => {
    if (currentList.includes(email)) setter(currentList.filter((e) => e !== email));
    else setter([...currentList, email]);
  };

  const resetFormAndState = () => {
    setSelectedFiles([]);
    setSelectedType(null);
    setFormData((s) => ({
      ...s,
      ccr_violation_type_id: null,
      violated_at: localISO,
      comment: "",
    }));
    setActiveStep(0);
  };

  const handleSendNotification = async () => {
    if (!notifyViolationId) return;

    try {
      setSendingNotify(true);

      await api.post(`/violations/${notifyViolationId}/send-notification`, {
        ownerEmails: selectedOwnerEmails,
        internalEmails: selectedInternalEmails,
        subject: emailPreview.subject,
        html: emailPreview.html,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "✅ Notification sent successfully",
      });

      setNotifyOpen(false);
      const doneId = notifyViolationId;
      setNotifyViolationId(null);

      resetFormAndState();
      onSuccess?.(doneId);
    } catch (err) {
      console.error("Send notification failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message:
          err?.response?.data?.message ||
          err.message ||
          "Failed to send notification",
      });
    } finally {
      setSendingNotify(false);
    }
  };

  const handleSkipNotification = () => {
    setSnackbar({
      open: true,
      severity: "success",
      message: "✅ Violation created (notification skipped)",
    });

    setNotifyOpen(false);
    const doneId = notifyViolationId;
    setNotifyViolationId(null);

    resetFormAndState();
    onSuccess?.(doneId);
  };

  const handleSubmit = async () => {
    if (!formData.property_id?.trim() || !formData.account_id?.trim()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Missing property or account ID",
      });
      return;
    }

    if (!formData.ccr_violation_type_id) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please select a violation type",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const createRes = await api.post("/addViolation", {
        ccr_violation_type_id: formData.ccr_violation_type_id,
        violated_at: formData.violated_at,
        comment: formData.comment,
        property_id: formData.property_id.trim(),
        account_id: formData.account_id.trim(),
        // cure_by is set server-side from the JWT token
      });

      const violationId = createRes.data?.id;
      if (!violationId) throw new Error("Missing violation id from response");

      if (selectedFiles.length > 0) {
        const fd = new FormData();
        fd.append("request_id", violationId);
        selectedFiles.forEach((f) => fd.append("upload_urls", f));
        await api.post("/violation-files", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const notificationRequired = createRes.data?.notificationRequired === true;
      if (notificationRequired) {
        const emails = createRes.data?.candidateEmails || { owner: [], internal: [] };
        const preview = createRes.data?.emailPreview || { subject: "", html: "" };

        setNotifyViolationId(violationId);
        setCandidateEmails(emails);
        setSelectedOwnerEmails(emails.owner || []);
        setSelectedInternalEmails(emails.internal || []);
        setEmailPreview(preview);
        setNotifyOpen(true);

        setSnackbar({
          open: true,
          severity: "success",
          message: "✅ Violation created. Review notification recipients.",
        });

        return;
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: "✅ Violation created successfully",
      });

      resetFormAndState();
      onSuccess?.(violationId);
    } catch (err) {
      console.error("Create violation failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message:
          err?.response?.data?.message ||
          err.message ||
          "Failed to create violation",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    ...pageCardSx,
    p: { xs: 1.5, sm: 2, md: 2.5 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fc",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.5,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.25,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Property Information
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Confirm the property and account details before continuing.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={formConfig?.field_labels?.account_id     || "Account ID"}
                  name="account_id"
                  value={formData.account_id}
                  InputProps={{ readOnly: true }}
                  sx={readonlyFieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={formConfig?.field_labels?.property_id    || "Property ID"}
                  name="property_id"
                  value={formData.property_id}
                  InputProps={{ readOnly: true }}
                  sx={readonlyFieldSx}
                />
              </Grid>
            </Grid>
          </Card>
        );

      case 1:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Violation Details
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Select the violation type and enter the related details.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Autocomplete
                  options={violationTypes}
                  getOptionLabel={(option) => option.title || ""}
                  value={selectedType}
                  onChange={(_, newValue) => {
                    setSelectedType(newValue);
                    setFormData((s) => ({
                      ...s,
                      ccr_violation_type_id: newValue?.id
                        ? parseInt(newValue.id, 10)
                        : null,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={formConfig?.field_labels?.violation_type || "Violation Type"}
                      required
                      sx={textFieldSx}
                    />
                  )}
                />

                {selectedType?.rule && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: "14px",
                      backgroundColor: "rgba(47,99,173,0.04)",
                      border: "1px solid rgba(47,99,173,0.08)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
                    >
                      Rule
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      {selectedType.rule}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {(formConfig?.fields?.violation_date ?? true) && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth type="datetime-local"
                    label={formConfig?.field_labels?.violation_date || "Violation Date"}
                    name="violated_at" value={formData.violated_at}
                    onChange={handleInputChange} InputLabelProps={{ shrink: true }}
                    required sx={textFieldSx} />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Opened By"
                  value={(() => { try { const u = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "{}"); return u?.Name || u?.name || u?.Username || u?.username || "Current User"; } catch { return "Current User"; } })()}
                  InputProps={{ readOnly: true }} sx={readonlyFieldSx}
                  helperText="Set automatically from your login" />
              </Grid>

              {(formConfig?.fields?.comment ?? true) && (
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={4}
                    label={formConfig?.field_labels?.comment || "Comment"}
                    name="comment" value={formData.comment}
                    onChange={handleInputChange}
                    placeholder="Add any notes about this violation..." sx={textFieldSx} />
                </Grid>
              )}
            </Grid>
          </Card>
        );

      case 2:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              {formConfig?.field_labels?.attachments || "Attachments"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Upload supporting images or documents for this violation.
            </Typography>
            {(formConfig?.fields?.attachments ?? true) ? (<React.Fragment>

            <Box
              {...getRootProps()}
              sx={{
                p: 3,
                border: "2px dashed rgba(47,99,173,0.25)",
                borderRadius: "16px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive
                  ? "rgba(47,99,173,0.08)"
                  : "rgba(47,99,173,0.02)",
                transition: "all 0.2s ease",
                mb: 2,
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 42, color: "#2f63ad", mb: 1 }} />
              <Typography sx={{ fontWeight: 600, color: "#223142", mb: 0.5 }}>
                {isDragActive
                  ? "Drop files here..."
                  : "Drag and drop files here, or click to upload"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Images, PDF, DOC, and DOCX files are supported.
              </Typography>
            </Box>

            {selectedFiles.length > 0 ? (
              <Box
                sx={{
                  border: "1px solid rgba(47,99,173,0.08)",
                  borderRadius: "14px",
                  overflow: "hidden",
                }}
              >
                {selectedFiles.map((file, idx) => (
                  <Box
                    key={`${file.name}-${idx}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1.1,
                      borderBottom:
                        idx !== selectedFiles.length - 1
                          ? "1px solid rgba(47,99,173,0.08)"
                          : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#223142",
                        maxWidth: "80%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(idx)}
                      sx={{
                        border: "1px solid rgba(220,38,38,0.12)",
                        borderRadius: "10px",
                        color: "#dc2626",
                        backgroundColor: "rgba(220,38,38,0.04)",
                        "&:hover": {
                          backgroundColor: "rgba(220,38,38,0.1)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "#8a94a6" }}>
                No files uploaded yet.
              </Typography>
            )}
            </React.Fragment>) : (
              <Typography variant="body2" sx={{ color:"#9ca3af", textAlign:"center", py:3 }}>
                File attachments are not available for this form.
              </Typography>
            )}
          </Card>
        );

      case 3: {
        const formattedDate = formData.violated_at
          ? format(new Date(formData.violated_at), "PPpp")
          : "N/A";

        return (
          <Card sx={sectionCardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Review Submission
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Confirm all details before creating the violation.
            </Typography>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Account ID
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {formData.account_id}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Property ID
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {formData.property_id}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Violation Type
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {selectedType?.title || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Date
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {formattedDate}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Comment
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 400, color: "#223142", whiteSpace: "pre-wrap" }}
                >
                  {formData.comment || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
                  Attachments
                </Typography>

                {selectedFiles.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    {selectedFiles.map((file, i) => {
                      const isImage = file.type?.startsWith("image/");
                      const previewUrl = isImage ? URL.createObjectURL(file) : null;

                      return (
                        <Box key={`${file.name}-${i}`} sx={{ textAlign: "center" }}>
                          {isImage ? (
                            <img
                              src={previewUrl}
                              alt={file.name}
                              style={{
                                width: 96,
                                height: 96,
                                objectFit: "cover",
                                borderRadius: 14,
                                border: "1px solid rgba(47,99,173,0.08)",
                              }}
                              onLoad={() => {
                                if (previewUrl) URL.revokeObjectURL(previewUrl);
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                border: "1px solid rgba(47,99,173,0.08)",
                                borderRadius: "12px",
                                backgroundColor: "rgba(47,99,173,0.03)",
                                maxWidth: 180,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6b778c",
                                  fontStyle: "italic",
                                }}
                                title={file.name}
                                noWrap
                              >
                                {file.name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="subtitle1" sx={{ fontWeight: 400, color: "#223142" }}>
                    None
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        );
      }

      default:
        return null;
    }
  };

  const sendDisabled =
    sendingNotify ||
    (selectedOwnerEmails.length === 0 && selectedInternalEmails.length === 0);

  const nextDisabled =
    isSubmitting ||
    (activeStep === 1 && !formData.ccr_violation_type_id) ||
    (activeStep === 0 && (!formData.account_id || !formData.property_id));

  return (
    <>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <WarningAmberIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              {formConfig?.title || "Add Violation"}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            {formConfig?.subtitle || "Create a new HOA violation record and attach any supporting files."}
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: "0.9rem",
              },
              "& .MuiStepLabel-label.Mui-active": {
                fontWeight: 700,
                color: "#223142",
              },
              "& .MuiStepLabel-label.Mui-completed": {
                fontWeight: 600,
              },
            }}
          >
            {(formConfig?.step_labels || DEFAULT_STEPS).map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {renderStepContent()}

          <Divider sx={{ my: 3, borderColor: "rgba(47,99,173,0.08)" }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              disabled={activeStep === 0 || isSubmitting}
              onClick={() => setActiveStep((s) => s - 1)}
              sx={secondaryButtonSx}
            >
              Back
            </Button>

            {activeStep === (formConfig?.step_labels || DEFAULT_STEPS).length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={primaryButtonSx}
              >
                {isSubmitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  formConfig?.submit_label || "Submit Violation"
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setActiveStep((s) => s + 1)}
                disabled={nextDisabled}
                sx={primaryButtonSx}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Card>

      <Dialog
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            background: "rgba(255,255,255,0.98)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            color: "#223142",
            pb: 1,
          }}
        >
          <NotificationsActiveIcon sx={{ color: "#2f63ad" }} />
          Send Notification?
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: "rgba(47,99,173,0.08)" }}>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Select which recipients should receive this violation notification.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: "#223142" }}>
              Owner Email
            </Typography>

            {candidateEmails.owner?.length ? (
              <FormGroup>
                {candidateEmails.owner.map((email) => (
                  <FormControlLabel
                    key={email}
                    control={
                      <Checkbox
                        checked={selectedOwnerEmails.includes(email)}
                        onChange={() =>
                          toggleEmail(setSelectedOwnerEmails, selectedOwnerEmails, email)
                        }
                      />
                    }
                    label={email}
                  />
                ))}
              </FormGroup>
            ) : (
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                No owner email found.
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: "#223142" }}>
              Internal Emails
            </Typography>

            {candidateEmails.internal?.length ? (
              <FormGroup>
                {candidateEmails.internal.map((email) => (
                  <FormControlLabel
                    key={email}
                    control={
                      <Checkbox
                        checked={selectedInternalEmails.includes(email)}
                        onChange={() =>
                          toggleEmail(
                            setSelectedInternalEmails,
                            selectedInternalEmails,
                            email
                          )
                        }
                      />
                    }
                    label={email}
                  />
                ))}
              </FormGroup>
            ) : (
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                No internal emails configured.
              </Typography>
            )}
          </Box>

          {emailPreview?.subject ? (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid rgba(47,99,173,0.08)",
                borderRadius: "14px",
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
                Preview Subject
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                {emailPreview.subject}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2.25, gap: 1 }}>
          <Button onClick={handleSkipNotification} variant="outlined" sx={secondaryButtonSx}>
            Skip
          </Button>

          <Button
            variant="contained"
            disabled={sendDisabled}
            onClick={handleSendNotification}
            sx={primaryButtonSx}
          >
            {sendingNotify ? "Sending..." : "Send Notification"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddViolationForm;